import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { trainings } from "../data/trainings";

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.05 },
  },
};

const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function Egitimler() {
  useEffect(() => {
    const url = "https://aysunseremet.com/egitimler";
    document.title =
      "Eğitimler — Uzm. Klinik Psikolog Aysun Şeremet | Webinar & Atölyeler";
    setMeta(
      "description",
      "Uzm. Klinik Psikolog Aysun Şeremet ile ebeveynlik, duygu düzenleme, öfke, kaygı, sınırlar ve kardeş ilişkileri üzerine uygulamalı webinarlar."
    );
    let c = document.querySelector('link[rel="canonical"]');
    if (!c) {
      c = document.createElement("link");
      c.rel = "canonical";
      document.head.appendChild(c);
    }
    c.href = url;
  }, []);

  return (
    <section className="bg-white px-4 pt-16 pb-28 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        {/* HERO — sade */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="mb-16 text-center"
        >
          <p className="text-xs font-medium uppercase tracking-[0.3em] text-pink-600">
            Webinar & Atölyeler
          </p>
          <h1 className="mt-4 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Eğitimler
          </h1>
          <div className="mx-auto mt-5 h-px w-16 bg-pink-400" />
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-gray-600">
            Uzm. Klinik Psikolog{" "}
            <span className="font-medium text-gray-900">Aysun Şeremet</span>{" "}
            tarafından hazırlanan, ebeveynlik ve ilişkiler üzerine uygulamalı
            webinar programları.
          </p>
        </motion.div>

        {/* GRID */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: false, amount: 0.1 }}
          className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {trainings.map((t, index) => {
            const num = String(index + 1).padStart(2, "0");
            return (
              <motion.article
                key={t.id}
                variants={item}
                className="group relative flex flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200 transition-all duration-300 hover:ring-pink-300 hover:shadow-lg"
              >
                <Link
                  to={`/egitimler/${t.id}`}
                  className="flex flex-1 flex-col p-7"
                >
                  {/* Header row */}
                  <div className="flex items-start justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pink-600">
                      Webinar
                    </span>
                    <span className="text-xs font-medium text-gray-300 tabular-nums">
                      {num}
                    </span>
                  </div>

                  {/* Title */}
                  <h2 className="mt-5 text-xl font-semibold leading-snug text-gray-900 group-hover:text-pink-700 transition-colors line-clamp-3">
                    {t.title}
                  </h2>

                  {/* Pink accent line */}
                  <div className="mt-4 h-px w-10 bg-pink-400 transition-all duration-300 group-hover:w-20" />

                  {/* Subtitle */}
                  {t.subtitle && (
                    <p className="mt-4 text-sm italic text-gray-500 line-clamp-2">
                      {t.subtitle}
                    </p>
                  )}

                  {/* Description */}
                  <p className="mt-3 text-sm leading-relaxed text-gray-600 line-clamp-3 flex-1">
                    {t.description}
                  </p>

                  {/* Meta */}
                  <div className="mt-6 flex items-center gap-4 text-xs text-gray-400">
                    {t.duration && (
                      <span className="inline-flex items-center gap-1.5">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="9" />
                          <path d="M12 7v5l3 2" strokeLinecap="round" />
                        </svg>
                        {t.duration}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5">
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="5" width="18" height="12" rx="2" />
                        <path d="M8 21h8" strokeLinecap="round" />
                      </svg>
                      Online
                    </span>
                  </div>

                  {/* CTA */}
                  <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-pink-600 group-hover:gap-2.5 transition-all">
                    Detaylı Bilgi
                    <span aria-hidden>→</span>
                  </div>
                </Link>
              </motion.article>
            );
          })}
        </motion.div>

        {/* CTA — sade pembe kutu */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="mt-20 rounded-2xl bg-pink-50 px-8 py-10 text-center ring-1 ring-pink-100 sm:px-12 sm:py-14"
        >
          <h3 className="text-2xl font-semibold text-gray-900 sm:text-3xl">
            Eğitimler hakkında bilgi almak ister misiniz?
          </h3>
          <p className="mx-auto mt-3 max-w-xl text-sm text-gray-600 sm:text-base">
            Tarih, ücret ve kontenjan bilgisi için iletişime geçebilir; özel
            ekip/kurum eğitimleri talep edebilirsiniz.
          </p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="https://wa.me/905372153407"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-pink-500 px-6 py-3 text-sm font-semibold text-white transition hover:bg-pink-600"
            >
              WhatsApp ile İletişim
            </a>
            <Link
              to="/iletisim"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-semibold text-gray-900 ring-1 ring-gray-200 transition hover:ring-pink-300"
            >
              İletişim Formu
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
